import React, { useState, useContext, useEffect } from 'react';
import { 
  Container, Typography, Box, TextField, MenuItem, Button, 
  Grid, FormControlLabel, Checkbox, Paper, Alert, Snackbar, CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Axios from 'axios';
import AxiosInstance from '../../AxiosInstance';
import { useTranslation } from 'react-i18next';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import StateContext from '../../Contexts/StateContext';
import { MasterCategories, CompanyCategories, DayLabourerCategories } from '../../Constants/BusinessOptions';
import LocationSelector from '../Common/LocationSelector';
import config, { environment } from '../../config';

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function MapEvents({ setCoordinates }) {
    const map = useMap();
    useEffect(() => {
        map.on('click', (e) => {
            setCoordinates(e.latlng.lat, e.latlng.lng);
        });
    }, [map, setCoordinates]);
    return null;
}

function MapFlyTo({ location }) {
    const map = useMap();
    useEffect(() => {
        if (location[0] && location[1]) {
            map.flyTo(location, 13);
        }
    }, [location, map]);
    return null;
}

function RegisterBusiness() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const GlobalState = useContext(StateContext);

  const [formData, setFormData] = useState({
    business_type: 'Master',
    name: '',
    description: '',
    phone_number: '',
    email: '',
    website: '',
    facebook_url: '',
    hourly_rate: '',
    fixed_price: '',
    is_urgent: false,
    brings_tools: false,
    capacity: 1,
    category: '',
    address: '',
    szek: '',
    kozsegvaros: '',
    telepules: '',
    latitude: 45.8643,
    longitude: 25.7848,
    geocodingError: '',
    geocodingSuccess: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // const [allSzekek, setAllSzekek] = useState([]);
  // const [allKozsegek, setAllKozsegek] = useState([]);
  // const [allTelepulesek, setAllTelepulesek] = useState([]);


  const businessTypes = [
    { value: 'Master', label: t('SubMenu.Masters', 'Mester') },
    { value: 'Company', label: t('SubMenu.Companies', 'Cég') },
    { value: 'DayLabourer', label: t('SubMenu.DayLabourers', 'Napszámos') }
  ];

  const getCategories = () => {
    let categories = [];
    switch (formData.business_type) {
      case 'Master': categories = [...MasterCategories]; break;
      case 'Company': categories = [...CompanyCategories]; break;
      case 'DayLabourer': categories = [...DayLabourerCategories]; break;
      default: categories = [];
    }
    return categories
      .map(cat => ({ ...cat, translatedLabel: cat.label.includes('.') ? t(cat.label, cat.label) : cat.label }))
      .sort((a, b) => a.translatedLabel.localeCompare(b.translatedLabel, i18n.language));
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGeocode = async () => {
    if (!formData.address) return;
    try {
      const response = await Axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(formData.address)}`);
      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        setFormData(prev => ({
          ...prev,
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
          geocodingSuccess: true,
          geocodingError: ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          geocodingError: t('Thematic.GeocodingNoResult', 'Nem található koordináta a megadott címhez.'),
          geocodingSuccess: false
        }));
      }
    } catch (e) {
      setFormData(prev => ({
        ...prev,
        geocodingError: t('Thematic.GeocodingError', 'Hiba történt a keresés során.'),
        geocodingSuccess: false
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Append category to description if needed for current filtering logic
    const finalDescription = formData.category 
      ? `${formData.category}\n\n${formData.description}`
      : formData.description;

    try {
      // Koordináták kezelése
      const lat = parseFloat(formData.latitude);
      const lon = parseFloat(formData.longitude);

      // Tisztítsuk meg az adatokat a küldés előtt
      const payload = {
        ...formData,
        description: finalDescription,
        // Biztosítsuk, hogy a helyszín adatok string formátumban vannak
        szek: String(formData.szek || ''),
        kozsegvaros: String(formData.kozsegvaros || ''),
        telepules: String(formData.telepules || ''),
        // Koordináták kerekítése és formázása
        latitude: !isNaN(lat) ? lat.toFixed(6) : null,
        longitude: !isNaN(lon) ? lon.toFixed(6) : null,
        owner: GlobalState.userId
      };

      // Távolítsuk el a segédmezőket a payload-ból
      delete payload.geocodingError;
      delete payload.geocodingSuccess;

      await AxiosInstance.post(config[environment].businessesEndpoint + 'create/', payload, {
        headers: {
          Authorization: `Bearer ${GlobalState.jwtAccessToken}`
        }
      });
      setSuccess(true);
      setTimeout(() => navigate(-1), 2000);
    } catch (err) {
      const errorData = err.response?.data;
      let errorMsg = "";
      if (errorData && typeof errorData === 'object') {
        if (errorData.detail && typeof errorData.detail === 'string') {
          errorMsg = errorData.detail;
        } else {
          errorMsg = JSON.stringify(errorData);
        }
      } else {
        errorMsg = err.message || t('Common.Error', 'Hiba történt a mentés során.');
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!GlobalState.userId) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning">
          {t('Administration.LoginRequired', 'A jelentkezéshez be kell jelentkeznie.')}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
          {t('Login.Title', 'Bejelentkezés')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4, mt: '50px' }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
          {t('Business.RegisterAsWorker')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          {t('Business.RegisterIntro', 'Add meg az adataidat, hogy a munkáltatók rátaláljanak a szolgáltatásaidra.')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {String(error)}
          </Alert>
        )}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{t('Common.SaveSuccess', 'Sikeres mentés!')}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t('Common.Type', 'Típus')}
                name="business_type"
                value={formData.business_type}
                onChange={handleChange}
                required
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {businessTypes.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t('Common.Category', 'Kategória')}
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {getCategories().map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.translatedLabel}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('Common.Name', 'Név / Megnevezés')}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('Common.Bio', 'Bemutatkozás')}
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder={t('Business.BioPlaceholder')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Administration.PhoneNumber', 'Telefonszám')}
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Administration.Email', 'Email cím')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('Business.Website', 'Honlap')}
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://www.weboldal.hu"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('SubMenu.FacebookUrl', 'Facebook profil URL')}
                name="facebook_url"
                value={formData.facebook_url}
                onChange={handleChange}
                placeholder="https://facebook.com/profilnev"
              />
            </Grid>

            <LocationSelector
              selectedSzek={formData.szek}
              selectedKozseg={formData.kozsegvaros}
              selectedTelepules={formData.telepules}
              onSzekChange={(val) => setFormData(prev => ({ ...prev, szek: val, kozsegvaros: '', telepules: '' }))}
              onKozsegChange={(val) => setFormData(prev => ({ ...prev, kozsegvaros: val, telepules: '' }))}
              onTelepulesChange={(val) => setFormData(prev => ({ ...prev, telepules: val }))}
            />

            <Grid item xs={12} sm={9}>
              <TextField
                fullWidth
                label={t('Common.Address')}
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGeocode}
                sx={{ height: '56px' }}
              >
                {t('Thematic.Geocode')}
              </Button>
            </Grid>
            {formData.geocodingError && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  {formData.geocodingError}
                </Alert>
              </Grid>
            )}
            {formData.geocodingSuccess && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mt: 1 }}>
                  {t('Thematic.GeocodingSuccess')}
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                                    {t('Common.Location')}
                                </Typography>
              <Box sx={{ height: '300px', width: '100%', mb: 2 }}>
                <MapContainer center={[formData.latitude, formData.longitude]} zoom={13} scrollWheelZoom={true} style={{ height: '100%', borderRadius: '8px' }}>
                  <TileLayer url={config[environment].osmTiles} />
                  <Marker position={[formData.latitude, formData.longitude]} icon={defaultIcon} />
                  <MapEvents setCoordinates={(lat, lng) => setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }))} />
                  <MapFlyTo location={[formData.latitude, formData.longitude]} />
                </MapContainer>
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label={t('Business.HourlyRate')}
                name="hourly_rate"
                value={formData.hourly_rate}
                onChange={handleChange}
              />
            </Grid>
            {formData.business_type === 'DayLabourer' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('Business.Capacity', 'Létszám / Kapacitás')}
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleChange}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="brings_tools" checked={formData.brings_tools} onChange={handleChange} />}
                label={t('Business.BringsTools', 'Hoz szerszámot')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Checkbox name="is_urgent" checked={formData.is_urgent} onChange={handleChange} />}
                label={t('Business.UrgentWork', 'Vállal sürgős munkát')}
              />
            </Grid>
          </Grid>

          {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              size="large" 
              disabled={loading}
              sx={{ bgcolor: 'var(--sznodarkblue)' }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('Common.Submit', 'Jelentkezés beküldése')}
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
              {t('Common.Cancel', 'Mégse')}
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar open={success} autoHideDuration={3000} onClose={() => setSuccess(false)}>
        <Alert severity="success" sx={{ width: '100%' }}>
          {t('Business.RegisterSuccess', 'Sikeresen regisztráltál munkavállalóként!')}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default RegisterBusiness;
